import { useState } from "react";
import { ChevronRight, ChevronDown, Plus } from "lucide-react";
import TimesheetEntryRow from "./TimesheetEntryRow";
import type { TimeEntry, Timesheet } from "@/lib/backend";
import type { Task } from "@/lib/types";

interface LineItem {
  key: string; // task_id or "desc::<hash>"
  task_id: string | null;
  description: string | null;
  entries: TimeEntry[]; // all entries for this line (one per day max)
  representative: TimeEntry; // the first entry, used for row identity
}

interface TimesheetProjectGroupProps {
  projectId: string;
  projectName: string;
  clientName?: string | null;
  city?: string | null;
  state?: string | null;
  entries: TimeEntry[];
  tasks: Task[];
  weekDates: string[]; // 7 YYYY-MM-DD strings
  timesheetStatus: Timesheet["status"];
  onSaveHours: (
    lineKey: string,
    projectId: string,
    taskId: string | null,
    description: string | null,
    entryDate: string,
    hours: number,
    existingEntryId: string | null
  ) => Promise<void>;
  onDeleteEntry: (entryId: string) => Promise<void>;
  onAddEntry: (projectId: string) => void;
  onTaskClick: (task: Task) => void;
}

function descriptionKey(d: string | null): string {
  return `desc::${(d ?? "").trim().toLowerCase()}`;
}

function groupEntriesIntoLines(entries: TimeEntry[]): LineItem[] {
  const lineMap = new Map<string, LineItem>();
  for (const entry of entries) {
    const key = entry.task_id ? entry.task_id : descriptionKey(entry.description);
    if (!lineMap.has(key)) {
      lineMap.set(key, {
        key,
        task_id: entry.task_id,
        description: entry.task_id ? null : entry.description,
        entries: [],
        representative: entry,
      });
    }
    lineMap.get(key)!.entries.push(entry);
  }
  return Array.from(lineMap.values());
}

export default function TimesheetProjectGroup({
  projectId,
  projectName,
  clientName,
  city,
  state,
  entries,
  tasks,
  weekDates,
  timesheetStatus,
  onSaveHours,
  onDeleteEntry,
  onAddEntry,
  onTaskClick,
}: TimesheetProjectGroupProps) {
  const [expanded, setExpanded] = useState(true);

  const lines = groupEntriesIntoLines(entries);
  const readOnly = timesheetStatus !== "draft";

  // Project-level daily totals (sum across all lines)
  const projectDailyTotals = weekDates.map((dateKey) =>
    entries.filter((e) => e.entry_date === dateKey).reduce((sum, e) => sum + Number(e.hours), 0)
  );
  const projectTotal = entries.reduce((sum, e) => sum + Number(e.hours), 0);

  return (
    <>
      {/* Project header row */}
      <tr
        className="border-b border-white/10 cursor-pointer hover:bg-white/[0.03] group"
        onClick={() => setExpanded((v) => !v)}
      >
        <td className="px-3 py-2 text-xs font-semibold text-white/90">
          <div className="flex items-center gap-1.5">
            {expanded
              ? <ChevronDown className="w-3 h-3 text-white/40 shrink-0" />
              : <ChevronRight className="w-3 h-3 text-white/40 shrink-0" />
            }
            <div className="flex flex-col min-w-0">
              {(clientName || city || state) && (
                <div className="flex items-center gap-1.5 text-[10px] text-white/40 font-normal leading-tight">
                  {clientName && <span className="truncate">{clientName}</span>}
                  {clientName && (city || state) && <span className="text-white/20">·</span>}
                  {(city || state) && (
                    <span className="truncate">
                      {[city, state].filter(Boolean).join(", ")}
                    </span>
                  )}
                </div>
              )}
              <span className="truncate">{projectName}</span>
              {!expanded && (
                <span className="text-white/30 text-[10px] font-normal">
                  {lines.length} {lines.length === 1 ? "entry" : "entries"}
                </span>
              )}
            </div>
          </div>
        </td>
        {weekDates.map((dateKey, i) => (
          <td key={dateKey} className="px-1 py-2 text-center text-xs text-white/50">
            {projectDailyTotals[i] > 0
              ? (projectDailyTotals[i] % 1 === 0 ? projectDailyTotals[i] : projectDailyTotals[i].toFixed(2))
              : <span className="text-white/20">—</span>
            }
          </td>
        ))}
        <td className="px-2 py-2 text-center text-xs font-semibold text-white/70">
          {projectTotal > 0
            ? (projectTotal % 1 === 0 ? projectTotal : projectTotal.toFixed(2))
            : <span className="text-white/20">—</span>
          }
        </td>
        <td />
      </tr>

      {/* Entry rows (only when expanded) */}
      {expanded && (
        <>
          {lines.map((line) => {
            const task = line.task_id ? (tasks.find((t) => t.id === line.task_id) ?? null) : null;
            return (
              <TimesheetEntryRow
                key={line.key}
                entry={line.representative}
                task={task}
                weekDates={weekDates}
                allEntries={line.entries}
                timesheetId={line.representative.timesheet_id}
                timesheetStatus={timesheetStatus}
                onSaveHours={(entryDate, hours, existingEntryId) =>
                  onSaveHours(
                    line.key,
                    projectId,
                    line.task_id,
                    line.description,
                    entryDate,
                    hours,
                    existingEntryId
                  )
                }
                onDelete={() => {
                  // Delete all entries for this line
                  Promise.all(line.entries.map((e) => onDeleteEntry(e.id)));
                }}
                onTaskClick={onTaskClick}
              />
            );
          })}

          {/* Add entry row */}
          {!readOnly && (
            <tr className="border-b border-white/5">
              <td colSpan={weekDates.length + 3} className="pl-8 py-1">
                <button
                  className="flex items-center gap-1 text-xs text-white/30 hover:text-white/60 transition-colors"
                  onClick={(e) => { e.stopPropagation(); onAddEntry(projectId); }}
                >
                  <Plus className="w-3 h-3" />
                  Add entry
                </button>
              </td>
            </tr>
          )}
        </>
      )}
    </>
  );
}
